import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import MainLayout from '../../components/layouts/MainLayout';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TablePagination,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';

import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ToysIcon from '@mui/icons-material/Toys';

import { CONTEXTS, CONTROLLERS, SERVICES } from 'figshelf-modules';

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'approved', label: 'Approved' },
  { value: 'pending', label: 'Pending' },
  { value: 'rejected', label: 'Rejected' },
];

const FigureManagement = () => {
  const api = useContext(CONTEXTS.APIContext);
  const auth = useContext(CONTEXTS.AuthContext);

  const adminController = useMemo(() => new CONTROLLERS.AdminController(api), [api]);
  const figureController = useMemo(() => new CONTROLLERS.FigureController(api), [api]);

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [totalItems, setTotalItems] = useState(0);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [originFilter, setOriginFilter] = useState('');
  const [characterFilter, setCharacterFilter] = useState('');

  const [selectedFigure, setSelectedFigure] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
  });

  const [deleteOpen, setDeleteOpen] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const parseStats = (raw) => {
    const payload = raw?.data ?? raw;
    const data = payload?.data ?? payload;
    return data || null;
  };

  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true);

      const cacheKey = SERVICES.makeAdminCacheKey({
        userId: auth?.user?.id,
        scope: 'figures:stats',
        params: { v: 1 },
      });

      const cached = await SERVICES.getCachedAdminList(cacheKey, { maxAgeMs: 2 * 60 * 1000 });
      if (cached?.stats) {
        setStats(cached.stats);
      }

      const res = await adminController.getFigureStats();
      const parsed = parseStats(res);
      setStats(parsed);
      await SERVICES.cacheAdminList(cacheKey, { stats: parsed }, { scope: 'figures:stats' });
    } catch (e) {
      console.error('Failed to fetch figure stats:', e);
    } finally {
      setStatsLoading(false);
    }
  }, [adminController, auth?.user?.id]);

  const fetchFigures = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage('');

      const params = {
        skip: page + 1,
        take: rowsPerPage,
        query: searchQuery.trim() || undefined,
        status: statusFilter || undefined,
        category: categoryFilter.trim() || undefined,
        origin: originFilter.trim() || undefined,
        character: characterFilter.trim() || undefined,
      };

      const cacheKey = SERVICES.makeAdminCacheKey({
        userId: auth?.user?.id,
        scope: 'figures:list',
        params,
      });

      const cached = await SERVICES.getCachedAdminList(cacheKey, { maxAgeMs: 2 * 60 * 1000 });
      if (Array.isArray(cached?.rows)) {
        setItems(cached.rows);
        setTotalItems(Number.isFinite(cached.totalItems) ? cached.totalItems : 0);
      }

      const res = await adminController.getFigures(params);
      const rows = res?.data ?? [];
      const meta = res?.meta ?? {};
      const total = meta?.totalItems ?? meta?.total ?? 0;

      setItems(Array.isArray(rows) ? rows : []);
      setTotalItems(Number.isFinite(total) ? total : 0);

      await SERVICES.cacheAdminList(
        cacheKey,
        { rows: Array.isArray(rows) ? rows : [], totalItems: Number.isFinite(total) ? total : 0 },
        { scope: 'figures:list' },
      );
    } catch (e) {
      console.error('Failed to fetch figures:', e);
      setErrorMessage(e?.response?.data?.message || e?.message || 'Failed to load figures');
      setItems([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [adminController, auth?.user?.id, page, rowsPerPage, searchQuery, statusFilter, categoryFilter, originFilter, characterFilter]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchFigures();
  }, [fetchFigures]);

  const handleOpenPreview = async (row) => {
    try {
      const id = row?.id;
      if (!id) return;
      const res = await figureController.getFigure(id);
      const full = res?.data?.data ?? res?.data ?? res;
      setSelectedFigure(full?.data ?? full);
      setPreviewOpen(true);
    } catch (e) {
      console.error('Failed to fetch figure:', e);
      showSnackbar('Failed to load figure', 'error');
    }
  };

  const handleOpenEdit = (row) => {
    setSelectedFigure(row);
    setEditForm({
      name: row?.name || '',
      description: row?.description || '',
    });
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      if (!selectedFigure?.id) return;
      await figureController.updateFigure(selectedFigure.id, {
        name: editForm.name,
        description: editForm.description,
      });
      showSnackbar('Update submitted (pending approval)', 'success');
      setEditOpen(false);
      fetchFigures();
      fetchStats();
    } catch (e) {
      console.error('Update figure failed:', e);
      showSnackbar(e?.response?.data?.message || e?.message || 'Failed to update figure', 'error');
    }
  };

  const handleOpenDelete = (row) => {
    setSelectedFigure(row);
    setDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      if (!selectedFigure?.id) return;
      await api.delete(`/figures/${selectedFigure.id}`);
      showSnackbar('Figure deleted', 'success');
      setDeleteOpen(false);
      fetchFigures();
      fetchStats();
    } catch (e) {
      console.error('Delete figure failed:', e);
      showSnackbar(e?.response?.data?.message || e?.message || 'Failed to delete figure', 'error');
    }
  };

  const getStatusColor = (status) => {
    const v = String(status || '').toLowerCase();
    if (v === 'approved') return 'success';
    if (v === 'pending') return 'warning';
    if (v === 'rejected') return 'error';
    return 'default';
  };

  const statsCards = [
    { label: 'Total', value: stats?.total ?? 0 },
    { label: 'Approved', value: stats?.approved ?? 0 },
    { label: 'Pending', value: stats?.pending ?? 0 },
    { label: 'Rejected', value: stats?.rejected ?? 0 },
  ];

  return (
    <MainLayout>
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h5" fontWeight={600} mb={1}>Figure Management</Typography>
            <Typography variant="subtitle2" color="text.secondary">Browse, filter, preview, update, and delete figures</Typography>
          </Box>
        </Box>

        <Grid container spacing={2} mb={3}>
          {statsLoading && !stats ? (
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Grid>
          ) : (
            statsCards.map((c) => (
              <Grid item xs={12} sm={6} md={3} key={c.label}>
                <Card sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="h6" fontWeight={800}>{Number(c.value).toLocaleString()}</Typography>
                        <Typography variant="body2" color="text.secondary">{c.label}</Typography>
                      </Box>
                      <Avatar sx={{ bgcolor: 'background.paper' }}>
                        <ToysIcon color="action" />
                      </Avatar>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>

        <Card sx={{ borderRadius: 3, mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1.5fr 220px 1fr 1fr 1fr', gap: 2 }}>
              <TextField
                label="Search"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
                size="small"
              />

              <FormControl size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
                >
                  {STATUS_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Category"
                value={categoryFilter}
                onChange={(e) => { setCategoryFilter(e.target.value); setPage(0); }}
                size="small"
              />

              <TextField
                label="Origin"
                value={originFilter}
                onChange={(e) => { setOriginFilter(e.target.value); setPage(0); }}
                size="small"
              />

              <TextField
                label="Character"
                value={characterFilter}
                onChange={(e) => { setCharacterFilter(e.target.value); setPage(0); }}
                size="small"
              />
            </Box>

            {errorMessage ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                {errorMessage}
              </Alert>
            ) : null}

            <TableContainer component={Paper} sx={{ boxShadow: 'none', mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Figure</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell>Origin</TableCell>
                    <TableCell>Character</TableCell>
                    <TableCell>Created By</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                        <CircularProgress size={28} />
                      </TableCell>
                    </TableRow>
                  ) : items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                        <Typography variant="body2" color="text.secondary">No figures found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar src={row?.figureImages?.[0]?.url || row?.imageUrl || undefined} sx={{ width: 36, height: 36 }}>
                              <ToysIcon fontSize="small" />
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={800} noWrap sx={{ maxWidth: 420 }}>
                                {row.name || 'Untitled'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {row.category || '—'}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={row.status || '—'} size="small" color={getStatusColor(row.status)} sx={{ textTransform: 'capitalize' }} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{row.company || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{row.origin || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{row.character || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{row.createdBy?.username || '—'}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => handleOpenPreview(row)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleOpenEdit(row)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleOpenDelete(row)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalItems}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[12, 24, 48]}
            />
          </CardContent>
        </Card>

        <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Figure Preview</DialogTitle>
          <DialogContent>
            {selectedFigure ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 2 }}>
                <Avatar
                  src={selectedFigure?.figureImages?.[0]?.url || selectedFigure?.imageUrl || undefined}
                  variant="rounded"
                  sx={{ width: 160, height: 160 }}
                />
                <Box>
                  <Typography variant="h6" fontWeight={900}>
                    {selectedFigure?.name || 'Untitled'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {selectedFigure?.description || '—'}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
                    {selectedFigure?.origin ? <Chip label={`Origin: ${selectedFigure.origin}`} size="small" /> : null}
                    {selectedFigure?.character ? <Chip label={`Character: ${selectedFigure.character}`} size="small" /> : null}
                    {selectedFigure?.category ? <Chip label={`Category: ${selectedFigure.category}`} size="small" /> : null}
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Update Figure</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Name"
              value={editForm.name}
              onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
              sx={{ mt: 1 }}
            />
            <TextField
              fullWidth
              label="Description"
              value={editForm.description}
              onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))}
              multiline
              rows={4}
              sx={{ mt: 2 }}
            />
            <Alert severity="info" sx={{ mt: 2 }}>
              Updates are submitted as a draft and may require approval.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveEdit}>Save</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Delete Figure</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              This will permanently delete the figure.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button color="error" variant="contained" onClick={handleConfirmDelete}>Delete</Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </MainLayout>
  );
};

export default FigureManagement;
